/**
 * CompleteProfilePage — shown after the first successful OTP login when
 * the customer doesn't have a name + delivery address yet.
 *
 * Saves to `customers.full_name` + a row in `customer_addresses` (set as
 * default). After save, navigate back to wherever the user was headed
 * (the `from` route param, defaulting to /checkout).
 *
 * Ported from Sushiki's CompleteProfilePage but stripped of the Leaflet
 * map + brand split. A map can be added later — for MVP a clean form is
 * enough.
 */

import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Container, Stack, TextField, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';

export const CompleteProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, refreshProfile, user, loading } = useAuth();
  const returnTo = (location.state as any)?.from || '/checkout';

  const [name, setName]         = useState('');
  const [street, setStreet]     = useState('');
  const [city, setCity]         = useState('');
  const [postcode, setPostcode] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (profile) {
      if (profile.name)     setName(profile.name);
      if (profile.street)   setStreet(profile.street);
      if (profile.city)     setCity(profile.city);
      if (profile.postcode) setPostcode(profile.postcode);
    }
  }, [profile]);

  // If the user is already complete (has a name AND a default address), skip
  // the form and bounce straight to where they were going.
  useEffect(() => {
    if (loading) return;
    if (profile && profile.name && profile.street) {
      navigate(returnTo, { replace: true });
    }
  }, [profile, loading, navigate, returnTo]);

  // No user → kick back to /auth.
  useEffect(() => {
    if (!loading && !user) navigate('/auth', { replace: true });
  }, [loading, user, navigate]);

  const save = async () => {
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!street.trim()) {
      setError('Please enter your address.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // 1. Save the name on the customers row.
      const { error: profErr } = await supabase.rpc('update_customer_profile', {
        p_full_name: name.trim(),
      });
      if (profErr) throw profErr;

      // 2. Save the default delivery address.
      const { error: addrErr } = await supabase.rpc('save_customer_default_address', {
        p_address_line: street.trim(),
        p_city:         city.trim() || null,
        p_postcode:     postcode.trim() || null,
        p_country:      'SA',
        p_label:        'Home',
      });
      if (addrErr) throw addrErr;

      await refreshProfile();
      navigate(returnTo, { replace: true });
    } catch (err: any) {
      console.error('[complete-profile] save failed:', err);
      setError(err?.message || 'Could not save your profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputSx = {
    '& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#fff' },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C896 !important' },
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 4, pb: 8 }}>
      <Box
        sx={{
          backgroundColor: 'rgba(10,37,64,0.6)',
          border: '1px solid rgba(0,200,150,0.2)',
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          color: '#fff',
        }}
      >
        <Stack spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Box component="img" src="/saheeh_logo_w.svg" alt="Saheeh" sx={{ height: 56 }} />
          <Typography sx={{ fontSize: '22px', fontWeight: 800 }}>
            Almost there
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', textAlign: 'center', maxWidth: 360 }}>
            Tell us your name and where to deliver. You can update this anytime from your account.
          </Typography>
        </Stack>

        <Stack spacing={2}>
          <TextField fullWidth required label="Full name" value={name}     onChange={(e) => setName(e.target.value)}     sx={inputSx} />
          <TextField fullWidth required label="Street, building, apt." value={street}   onChange={(e) => setStreet(e.target.value)}   sx={inputSx} multiline minRows={2} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth label="City"     value={city}     onChange={(e) => setCity(e.target.value)}     sx={inputSx} />
            <TextField fullWidth label="Postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} sx={inputSx} />
          </Stack>

          {error && (
            <Typography sx={{ color: '#ef5350', fontSize: '13px', textAlign: 'center' }}>{error}</Typography>
          )}

          <Button
            onClick={save}
            disabled={saving || !name.trim() || !street.trim()}
            sx={{
              py: 1.25,
              background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
              color: '#0A2540',
              fontWeight: 700,
              fontSize: '15px',
              '&:hover': { background: 'linear-gradient(135deg, #1AD4A6 0%, #00B387 100%)' },
              '&:disabled': { opacity: 0.5, color: 'rgba(10,37,64,0.7)' },
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#0A2540' }} /> : 'Save & continue'}
          </Button>

          <Button
            onClick={() => navigate(returnTo, { replace: true })}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '13px' }}
          >
            Skip for now
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};
